import { useEffect, useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import { SeletorMultiplo, CartaoTotal } from './RelatorioConsumoArla'

interface Mes { mes: number; valores: Record<number, number>; notasPorAno: Record<number, number>; atual: number; anterior: number; notasAtual: number; notasAnterior: number; variacao: number | null }
interface ClienteAbc { posicao: number; cli_cod: string; cliente: string; cidade: string; valor: number; notas: number; pct: number; pctAcum: number; classe: 'A' | 'B' | 'C' }
interface Resultado {
  anos: number[]; ano: number; anoComparado: number | null; mesAtual: number | null; meses: Mes[]; clientes: ClienteAbc[]
  totais: { atual: number; anterior: number; anteriorAteMes: number; variacaoAteMes: number | null; notas: number; ticketMedio: number; clientes: number; classeA: { clientes: number; pct: number } }
}

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const TIPOS = [{ cod: 'NFE', des: 'NF-e' }, { cod: 'CTEOS', des: 'CT-e OS' }, { cod: 'NFSE', des: 'NFS-e' }]
const fmt = (v: number, casas: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: casas, maximumFractionDigits: casas })
const fmtVar = (v: number | null) => (v == null ? '—' : `${v > 0 ? '+' : ''}${fmt(v, 1)}%`)
const compacto = (v: number) => new Intl.NumberFormat('pt-BR', { notation: 'compact', maximumFractionDigits: 1 }).format(v)
const CORES_CLASSE: Record<string, { bg: string; fg: string }> = { A: { bg: '#dbeafe', fg: '#1e3a8a' }, B: { bg: '#ffedd5', fg: '#9a3412' }, C: { bg: '#f1f5f9', fg: '#475569' } }

function escalaY(max: number) {
  if (max <= 0) return { topo: 1, passo: 0.25 }
  const bruto = max / 4
  const mag = Math.pow(10, Math.floor(Math.log10(bruto)))
  const passo = [1, 2, 2.5, 5, 10].map(m => m * mag).find(p => p >= bruto) || 10 * mag
  return { topo: Math.ceil(max / passo) * passo, passo }
}

// Cor de cada ano: principal azul-escuro, anteriores em tons mais claros (mais antigo = mais claro).
function coresAnos(anos: number[]): Record<number, string> {
  const claros = ['#e2e8f0', '#cbd5e1', '#94a3b8', '#64748b']
  const antigos = anos.slice(0, -1)
  const out: Record<number, string> = {}
  antigos.forEach((a, i) => { out[a] = claros[Math.max(0, claros.length - antigos.length + i)] })
  out[anos[anos.length - 1]] = '#1b3f7b'
  return out
}

// Colunas agrupadas, uma por ano escolhido; maior mês do ano mais recente em laranja. Várias séries → legenda.
function GraficoComparativo({ res }: { res: Resultado }) {
  const [hover, setHover] = useState<number | null>(null)
  const cores = coresAnos(res.anos)
  const W = 800, H = 210, ml = 56, mr = 12, mt = 16, mb = 30
  const pw = W - ml - mr, ph = H - mt - mb
  const maior = Math.max(...res.meses.flatMap(m => res.anos.map(a => m.valores[a] || 0)), 0)
  const maxAtual = Math.max(...res.meses.map(m => m.atual))
  const idxMax = maxAtual > 0 ? res.meses.findIndex(m => m.atual === maxAtual) : -1
  const { topo, passo } = escalaY(maior)
  const y = (v: number) => mt + ph - (v / topo) * ph
  const banda = pw / 12
  const barra = Math.min(16, (banda * 0.72) / res.anos.length)
  const ticks: number[] = []
  for (let t = 0; t <= topo + 1e-9; t += passo) ticks.push(t)
  const coluna = (x: number, v: number, cor: string, key: string) => {
    const topoB = y(v), base = y(0), h = Math.max(base - topoB, v > 0 ? 1 : 0)
    if (!h) return null
    const r = Math.min(4, h, barra / 2)
    return <path key={key} d={`M${x},${base} V${topoB + r} Q${x},${topoB} ${x + r},${topoB} H${x + barra - r} Q${x + barra},${topoB} ${x + barra},${topoB + r} V${base} Z`} fill={cor} />
  }
  const legenda = [...res.anos.map(a => ({ txt: String(a), cor: cores[a] })), { txt: `maior mês de ${res.ano}`, cor: '#ea580c' }]

  return (
    <div style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: '1rem', fontSize: '0.74rem', color: '#475569', marginBottom: 4, flexWrap: 'wrap' }}>
        {legenda.map(l => (
          <span key={l.txt} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: l.cor }} /> {l.txt}</span>
        ))}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label={`Faturamento mensal de ${res.anos.join(', ')}`}>
        {ticks.map(t => (
          <g key={t}>
            <line x1={ml} x2={W - mr} y1={y(t)} y2={y(t)} stroke="#e5e7eb" strokeWidth={1} />
            <text x={ml - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill="#64748b">{compacto(t)}</text>
          </g>
        ))}
        {res.meses.map((m, i) => {
          const cx = ml + banda * i + banda / 2
          const larguraGrupo = res.anos.length * (barra + 2) - 2
          const xPrincipal = cx - larguraGrupo / 2 + (res.anos.length - 1) * (barra + 2)
          return (
            <g key={m.mes}>
              {res.anos.map((a, k) => {
                const principal = a === res.ano
                const cor = principal && i === idxMax ? (hover === i ? '#c2410c' : '#ea580c') : principal && hover === i ? '#14547f' : cores[a]
                return coluna(cx - larguraGrupo / 2 + k * (barra + 2), m.valores[a] || 0, cor, String(a))
              })}
              {i === idxMax && <text x={xPrincipal + barra / 2} y={y(m.atual) - 6} textAnchor="middle" fontSize={11} fontWeight={700} fill="#0f172a">{compacto(m.atual)}</text>}
              <text x={cx} y={H - mb + 16} textAnchor="middle" fontSize={11} fill="#475569">{MESES[i].slice(0, 3)}</text>
              <rect x={ml + banda * i} y={mt} width={banda} height={ph} fill="transparent" onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)} />
            </g>
          )
        })}
        <line x1={ml} x2={W - mr} y1={y(0)} y2={y(0)} stroke="#cbd5e1" strokeWidth={1} />
      </svg>
      {hover != null && (() => {
        const m = res.meses[hover]
        return (
          <div style={{
            position: 'absolute', pointerEvents: 'none', top: 24, left: `${((ml + banda * hover + banda / 2) / W) * 100}%`, transform: 'translateX(-50%)',
            background: '#0f172a', color: '#fff', borderRadius: 8, padding: '0.45rem 0.65rem', fontSize: '0.78rem', whiteSpace: 'nowrap', boxShadow: '0 6px 16px -6px rgba(0,0,0,0.4)',
          }}>
            <div style={{ fontWeight: 800, marginBottom: 2 }}>{MESES[hover]}</div>
            {[...res.anos].reverse().map(a => (
              <div key={a}><span style={{ display: 'inline-block', width: 10, height: 2, background: a === res.ano ? '#60a5fa' : cores[a], marginRight: 6, verticalAlign: 'middle' }} /><b>R$ {fmt(m.valores[a] || 0, 2)}</b> em {a}</div>
            ))}
            {res.anoComparado != null && <div style={{ color: '#cbd5e1' }}>Variação {res.ano} x {res.anoComparado}: {fmtVar(m.variacao)}</div>}
          </div>
        )
      })()}
    </div>
  )
}

// Evolução mensal do faturamento (ano x ano anterior) e curva ABC de clientes.
export default function RelatorioEvolucaoFaturamento() {
  const { empresas } = useEmpresa()
  const anoHoje = new Date().getFullYear()
  const [anosDisponiveis, setAnosDisponiveis] = useState<number[]>([anoHoje, anoHoje - 1])
  const [anosSel, setAnosSel] = useState<string[]>([String(anoHoje - 1), String(anoHoje)])
  useEffect(() => { api.get('/relatorios/evolucao-faturamento/anos').then(r => { if (r.data?.length) setAnosDisponiveis(r.data) }).catch(() => {}) }, [])
  const [empresasSel, setEmpresasSel] = useState<string[]>([])
  const [tiposSel, setTiposSel] = useState<string[]>([])
  const [graficosPdf, setGraficosPdf] = useState(true)
  const [res, setRes] = useState<Resultado | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')
  const [somenteA, setSomenteA] = useState(false)

  const params = () => ({ anos: anosSel.join(','), empresas: empresasSel.join(','), tipos: tiposSel.join(',') })

  // Até 5 anos (gráfico legível); nenhum marcado = ano atual x anterior.
  function mudarAnos(sel: string[]) {
    setErro('')
    if (sel.length > 5) { setErro('Escolha no máximo 5 anos.'); return }
    setAnosSel(sel)
  }

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/relatorios/evolucao-faturamento', { params: params() })).data) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/relatorios/evolucao-faturamento/${formato}`, { params: { ...params(), graficos: graficosPdf ? '1' : '0' }, responseType: 'blob' })
      const tipo = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipo }))
      if (formato === 'pdf') window.open(url, '_blank')
      else { const a = document.createElement('a'); a.href = url; a.download = `evolucao-faturamento-${[...anosSel].sort().join('-') || anoHoje}.xlsx`; a.click(); URL.revokeObjectURL(url) }
    } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    finally { setGerando('') }
  }

  const th: React.CSSProperties = { padding: '0.6rem 0.8rem', textAlign: 'right', fontSize: '0.72rem', fontWeight: 700, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.5rem 0.8rem', textAlign: 'right', color: '#374151', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', borderBottom: '1px solid #eef2f7', fontSize: '0.85rem' }
  const corVar = (v: number | null) => (v == null ? '#94a3b8' : v >= 0 ? '#047857' : '#b91c1c')
  const clientesVisiveis = res ? (somenteA ? res.clientes.filter(c => c.classe === 'A') : res.clientes) : []

  return (
    <div>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1.1rem', color: '#1a6ea8' }}>Evolução do Faturamento</h2>
        <div style={{ background: '#f7faff', borderLeft: '4px solid #1a6ea8', borderRadius: 8, padding: '0.9rem 1.1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.9rem', alignItems: 'flex-end' }}>
            <div className="field" style={{ width: 200, flex: '0 0 auto', margin: 0 }}>
              <label>Anos</label>
              <SeletorMultiplo opcoes={anosDisponiveis.map(a => ({ cod: String(a), des: String(a) }))}
                selecionados={anosSel} onChange={mudarAnos} placeholderBusca="Buscar ano..." />
            </div>
            <div className="field" style={{ width: 240, flex: '0 0 auto', margin: 0 }}>
              <label>Empresas</label>
              <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
                selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
            </div>
            <div className="field" style={{ width: 200, flex: '0 0 auto', margin: 0 }}>
              <label>Documentos</label>
              <SeletorMultiplo opcoes={TIPOS} selecionados={tiposSel} onChange={setTiposSel} placeholderBusca="Buscar..." />
            </div>
          </div>
        </div>
        <p style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '1.2rem' }}>
          Compara os anos escolhidos mês a mês (até 5; sem nenhum marcado, ano atual x anterior). A variação, o total e a curva ABC usam o ano mais recente. Só notas autorizadas, pela data de emissão.
        </p>
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', color: '#92400e', borderRadius: 6, padding: '0.45rem 0.75rem', fontSize: '0.8rem', marginTop: '-0.6rem', marginBottom: '1.2rem' }}>
          ⚠ As notas de devolução não são listadas neste relatório, pois devolução não é faturamento.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar relatório'}</button>
          <button type="button" onClick={() => exportar('pdf')} disabled={!!gerando} style={{ padding: '0.5rem 1rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', opacity: gerando === 'pdf' ? 0.7 : 1 }}>
            {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.78rem', color: '#475569', cursor: 'pointer' }}>
            <input type="checkbox" checked={graficosPdf} onChange={e => setGraficosPdf(e.target.checked)} /> Gráfico no PDF
          </label>
          <button type="button" onClick={() => exportar('excel')} disabled={!!gerando} style={{ padding: '0.5rem 1rem', background: '#15803d', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', opacity: gerando === 'excel' ? 0.7 : 1 }}>
            {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
        {erro && <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: 8 }}>{erro}</p>}
      </div>

      {res && (
        <>
          <div className="card" style={{ marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.7rem' }}>Mês a mês — {res.anos.join(' x ')}</h3>
            <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={{ ...th, textAlign: 'left' }}>Mês</th>
                  {[...res.anos].reverse().map(a => <th key={a} style={th}>{a}</th>)}
                  {res.anoComparado != null && <th style={th}>Var. {res.ano} x {res.anoComparado}</th>}
                  <th style={th}>Notas {res.ano}</th>
                </tr></thead>
                <tbody>
                  {res.meses.map((m, i) => (
                    <tr key={m.mes} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{MESES[m.mes - 1]}</td>
                      {[...res.anos].reverse().map(a => (
                        <td key={a} style={{ ...td, color: a === res.ano ? '#111827' : '#64748b', fontWeight: a === res.ano ? 600 : 400 }}>{fmt(m.valores[a] || 0, 2)}</td>
                      ))}
                      {res.anoComparado != null && <td style={{ ...td, color: corVar(m.variacao), fontWeight: 700 }}>{fmtVar(m.variacao)}</td>}
                      <td style={td}>{m.notasAtual}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#e8eef8' }}>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 800 }}>Total</td>
                    {[...res.anos].reverse().map(a => (
                      <td key={a} style={{ ...td, fontWeight: 800 }}>{fmt(res.meses.reduce((s, m) => s + (m.valores[a] || 0), 0), 2)}</td>
                    ))}
                    {res.anoComparado != null && <td style={td} />}
                    <td style={{ ...td, fontWeight: 800 }}>{res.totais.notas}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ borderRadius: 12, padding: '1rem 1.1rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', marginTop: '1rem' }}>
              <div style={{ color: '#fff', fontSize: '1rem', fontWeight: 800, marginBottom: '0.85rem' }}>TOTAL GERAL</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.7rem' }}>
                <CartaoTotal titulo={`Faturado ${res.ano}`} cor="#1b3f7b" itens={[{ rotulo: res.mesAtual ? `jan a ${MESES[res.mesAtual - 1].slice(0, 3).toLowerCase()}` : 'no ano', valor: `R$ ${fmt(res.totais.atual, 2)}` }]} />
                {res.anoComparado != null && (
                  <CartaoTotal titulo={`${res.anoComparado} no mesmo período`} cor="#94a3b8" itens={[{ rotulo: res.mesAtual ? `jan a ${MESES[res.mesAtual - 1].slice(0, 3).toLowerCase()}` : 'no ano', valor: `R$ ${fmt(res.totais.anteriorAteMes, 2)}` }]} />
                )}
                {res.anoComparado != null && (
                  <CartaoTotal titulo="Variação" cor={res.totais.variacaoAteMes != null && res.totais.variacaoAteMes < 0 ? '#b91c1c' : '#047857'} itens={[{ rotulo: `${res.ano} x ${res.anoComparado}`, valor: fmtVar(res.totais.variacaoAteMes) }]} />
                )}
                <CartaoTotal titulo="Notas / Ticket médio" cor="#7c3aed" itens={[{ rotulo: 'Notas', valor: fmt(res.totais.notas, 0) }, { rotulo: 'Ticket', valor: `R$ ${fmt(res.totais.ticketMedio, 2)}` }]} />
              </div>
            </div>

            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.7rem 0.9rem', background: '#fff', maxWidth: 860, marginTop: '1rem' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a', marginBottom: '0.3rem' }}>Faturamento mensal</div>
              <GraficoComparativo res={res} />
            </div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.7rem' }}>
              <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#0f172a' }}>Curva ABC de clientes — {res.ano}</h3>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.8rem', color: '#475569', cursor: 'pointer' }}>
                <input type="checkbox" checked={somenteA} onChange={e => setSomenteA(e.target.checked)} /> Só classe A
              </label>
            </div>
            <p style={{ fontSize: '0.82rem', color: '#475569', marginBottom: '0.8rem' }}>
              <b>{res.totais.classeA.clientes}</b> de <b>{res.totais.clientes}</b> clientes (classe A) somam <b>{fmt(res.totais.classeA.pct, 1)}%</b> do faturamento.
              B leva até 95% e C é o restante.
            </p>
            <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>#</th><th style={{ ...th, textAlign: 'left' }}>Cliente</th><th style={{ ...th, textAlign: 'left' }}>Cidade</th>
                  <th style={th}>Valor (R$)</th><th style={th}>Notas</th><th style={th}>%</th><th style={th}>% acum.</th><th style={{ ...th, textAlign: 'center' }}>Classe</th>
                </tr></thead>
                <tbody>
                  {clientesVisiveis.map((c, i) => (
                    <tr key={c.cli_cod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                      <td style={{ ...td, color: '#94a3b8' }}>{c.posicao}</td>
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600, whiteSpace: 'normal' }}>{c.cliente}</td>
                      <td style={{ ...td, textAlign: 'left', color: '#64748b' }}>{c.cidade || '—'}</td>
                      <td style={td}>{fmt(c.valor, 2)}</td>
                      <td style={td}>{c.notas}</td>
                      <td style={td}>{fmt(c.pct, 2)}</td>
                      <td style={td}>{fmt(c.pctAcum, 2)}</td>
                      <td style={{ ...td, textAlign: 'center' }}>
                        <span style={{ display: 'inline-block', minWidth: 22, padding: '0.1rem 0.45rem', borderRadius: 999, fontWeight: 800, fontSize: '0.75rem', background: CORES_CLASSE[c.classe].bg, color: CORES_CLASSE[c.classe].fg }}>{c.classe}</span>
                      </td>
                    </tr>
                  ))}
                  {clientesVisiveis.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: '#94a3b8' }}>Nenhuma nota autorizada no ano.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
